import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { isPaid } from "@/lib/planActions";

export const baseRecipeId = (id) => (id || "").split("__no_")[0];

function usePaidGate() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const paid = isPaid(user);
  const gate = () => {
    if (paid) return true;
    toast(t("premium_feature"), { action: { label: t("pricing"), onClick: () => nav("/pricing") } });
    return false;
  };
  const onError = (e) => {
    if (e.response?.status === 402) gate();
    else toast.error(e.response?.data?.detail || "Error");
  };
  return { paid, gate, onError, user };
}

// Favourites (Plus/Premium): heart toggle + shared cache used by library, recipe page and plan cards
export function useFavorites() {
  const qc = useQueryClient();
  const { paid, gate, onError, user } = usePaidGate();
  const { data } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data.favorites, enabled: !!user });
  const favorites = data || [];
  const mutation = useMutation({
    mutationFn: (recipe_id) => api.post("/favorites/toggle", { recipe_id }),
    onSuccess: ({ data }) => {
      qc.setQueryData(["favorites"], data.favorites);
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["recipe"] });
    },
    onError,
  });
  return {
    paid, favorites,
    isFavorite: (id) => favorites.includes(baseRecipeId(id)),
    toggle: (id) => { if (gate()) mutation.mutate(baseRecipeId(id)); },
    busy: mutation.isPending,
  };
}

// Household note (rating 1-5 + short comment) on a recipe, one per account
export function useRecipeNote(recipeId) {
  const qc = useQueryClient();
  const { t } = useLang();
  const { paid, gate, onError } = usePaidGate();
  const id = baseRecipeId(recipeId);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
    qc.invalidateQueries({ queryKey: ["recipes"] });
  };
  const save = useMutation({
    mutationFn: (body) => api.put(`/recipe-notes/${id}`, body),
    onSuccess: () => { refresh(); toast.success(t("note_saved")); },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/recipe-notes/${id}`),
    onSuccess: () => { refresh(); toast.success(t("note_deleted")); },
    onError,
  });
  return {
    paid,
    save: (body) => { if (gate()) save.mutate(body); },
    remove: () => { if (gate()) remove.mutate(); },
    busy: save.isPending || remove.isPending,
  };
}
